import { Box, Flex } from '@chakra-ui/layout';
import React from 'react';
import { UseFieldArrayReturn, useFieldArray } from 'react-hook-form';

import { FormFields } from './types';
import { Typography } from 'packages/common-frontend/components/Typography';
import { AddBankAccountButton } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/parties/AddBankAccountButton';
import { useBankAccountDiffResult } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/parties/hooks';
import { useConfirmDeleteContext } from '~/core/routes/settings/implementations/common/ConfirmDeleteModalContext';
import { InputCardWithDiff } from '~/core/routes/settings/implementations/common/DiffInputs';
import { Organization } from '~/core/routes/settings/implementations/common/types';

const BankAccount = ({
  bankAccountFieldArray,
  bankAccountIndex,
  partyType,
  party,
}: {
  bankAccountFieldArray: UseFieldArrayReturn<
    FormFields,
    `${'wipForm.borrowers' | 'wipForm.capitalProviders'}.${number}.bankAccounts`
  >;
  bankAccountIndex: number;
  partyType: 'borrowers' | 'capitalProviders';
  party: Organization;
}) => {
  const { showConfirmDeleteModal } = useConfirmDeleteContext();

  const bankAccount = bankAccountFieldArray.fields[bankAccountIndex];
  const diff = useBankAccountDiffResult(partyType, party.id, bankAccount.id);

  return (
    <InputCardWithDiff
      diff={diff?.diffResult}
      deleteProperty={() =>
        showConfirmDeleteModal('Bank Account', () =>
          bankAccountFieldArray.remove(bankAccountIndex),
        )
      }
    >
      {/* TODO: consider showing more properties here. */}
      <Flex my="0.5rem" direction="column">
        <Typography variant="base" fontWeight="semibold">
          Name
        </Typography>
        <Typography variant="base-muted">{bankAccount.name}</Typography>
      </Flex>
    </InputCardWithDiff>
  );
};

export const BankAccounts = ({
  partyType,
  party,
  parentName,
}: {
  partyType: 'borrowers' | 'capitalProviders';
  party: Organization;
  parentName: `${'wipForm.borrowers' | 'wipForm.capitalProviders'}.${number}`;
}) => {
  // This still provides type safety - we're just not able to infer
  // `typeof bankAccountPath` automatically.
  const bankAccountPath = `${parentName}.bankAccounts` as const;
  const bankAccounts = useFieldArray<
    FormFields,
    typeof bankAccountPath,
    'unusedThrowawayId'
  >({
    name: bankAccountPath,
    // This defaults to 'id', which causes react-hook-form to overwrite the real
    // `id` field on the bank account.
    keyName: 'unusedThrowawayId',
  });

  return (
    <>
      <Flex mt="1.5rem" justify="space-between">
        <Box>
          <Box>
            <Typography variant="base" fontWeight="bold">
              Bank Accounts
            </Typography>
          </Box>
          {partyType === 'borrowers' && (
            <Typography variant="base-muted">
              Each borrower must have (at minimum) a collections account and a
              parent operating account.
            </Typography>
          )}
          {partyType === 'capitalProviders' && (
            <Typography variant="base-muted">
              Each lender must have a capital provider funding account.
            </Typography>
          )}
        </Box>
        <AddBankAccountButton
          partyType={partyType}
          party={party}
          onAddBankAccount={(ba) => bankAccounts.append(ba)}
        />
      </Flex>
      <Box>
        {bankAccounts.fields.map((field, index) => (
          <BankAccount
            key={field.id}
            bankAccountFieldArray={bankAccounts}
            bankAccountIndex={index}
            partyType={partyType}
            party={party}
          />
        ))}
      </Box>
    </>
  );
};
